import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, User, Users, Zap, TrendingUp, ArrowLeft, Loader2, Flag, CheckCircle2, Trophy } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/districts`;

const Stat = ({ icon: Icon, value, label, testid }) => (
  <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm" data-testid={testid}>
    <Icon className="mb-1 h-4 w-4 text-green-200" />
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-green-100">{label}</p>
  </div>
);

const OrganiserForm = ({ district }) => {
  const { t } = useLanguage();
  const [f, setF] = useState({ name: '', mobile: '', upazila: '', message: '' });
  const [state, setState] = useState('idle');
  const submit = async (e) => {
    e.preventDefault();
    setState('busy');
    const res = await fetch(`${API}/${encodeURIComponent(district)}/organiser`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) }).catch(() => null);
    setState(res?.ok ? 'done' : 'error');
  };
  if (state === 'done') return <p className="flex items-center gap-2 rounded-xl bg-green-50 p-4 text-sm font-semibold text-green-700" data-testid="organiser-success"><CheckCircle2 className="h-5 w-5" /> {t({ en: 'Thank you! Our team will call you soon.', bn: 'ধন্যবাদ! আমাদের টিম শীঘ্রই আপনাকে কল করবে।' })}</p>;
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="organiser-form">
      <Input required placeholder={t({ en: 'Your name', bn: 'আপনার নাম' })} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="organiser-name" />
      <Input required inputMode="tel" placeholder="01XXXXXXXXX" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="organiser-mobile" />
      <Input placeholder={t({ en: 'Upazila / Thana (optional)', bn: 'উপজেলা / থানা (ঐচ্ছিক)' })} value={f.upazila} onChange={(e) => setF({ ...f, upazila: e.target.value })} data-testid="organiser-upazila" />
      <Button type="submit" disabled={state === 'busy'} className="w-full bg-party-red hover:bg-red-700" data-testid="organiser-submit">
        {state === 'busy' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Flag className="mr-2 h-4 w-4" />}{t({ en: 'I want to organise', bn: 'আমি সংগঠক হতে চাই' })}
      </Button>
      {state === 'error' && <p className="text-sm text-red-600" data-testid="organiser-error">{t({ en: 'Could not submit. Try again.', bn: 'জমা দেওয়া যায়নি। আবার চেষ্টা করুন।' })}</p>}
    </form>
  );
};

const InfoRow = ({ icon: Icon, label, value, href, testid }) => value ? (
  <div className="flex items-start gap-3" data-testid={testid}>
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-party-green" />
    <div><p className="text-xs text-gray-400">{label}</p>{href ? <a href={href} className="font-medium text-gray-900 hover:text-party-red">{value}</a> : <p className="font-medium text-gray-900">{value}</p>}</div>
  </div>
) : null;

export default function DistrictDetail() {
  const { name } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [d, setD] = useState(undefined);

  useEffect(() => {
    fetch(`${API}/${encodeURIComponent(decodeURIComponent(name))}`).then(r => (r.ok ? r.json() : null)).then(setD).catch(() => setD(null));
  }, [name]);

  if (d === undefined) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-party-red" /></div>;
  if (d === null) return <div className="py-24 text-center text-gray-500" data-testid="district-not-found">{t({ en: 'District not found.', bn: 'জেলা পাওয়া যায়নি।' })}</div>;
  const c = d.chapter || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="district-detail-page">
      <section className="bg-gradient-to-br from-party-green via-emerald-700 to-gray-900 py-12 sm:py-16">
        <div className="mx-auto max-w-6xl px-4">
          <button onClick={() => navigate('/districts')} className="mb-6 flex w-fit items-center gap-1 text-sm text-green-100 hover:text-white" data-testid="district-back-btn"><ArrowLeft className="h-4 w-4" /> {t({ en: 'All districts', bn: 'সব জেলা' })}</button>
          <span className={`mb-3 inline-block rounded-full px-3 py-1 text-xs font-bold ${d.active ? 'bg-white text-party-green' : 'bg-amber-300 text-amber-900'}`} data-testid="district-status">{d.active ? t({ en: 'Active Chapter', bn: 'সক্রিয় শাখা' }) : t({ en: 'Chapter forming', bn: 'শাখা গঠনাধীন' })}</span>
          <h1 className="text-4xl font-bold text-white sm:text-5xl lg:text-6xl" data-testid="district-name">NDP {d.district}</h1>
          <div className="mt-8 grid max-w-2xl grid-cols-3 gap-3">
            <Stat icon={Users} value={d.stats.members} label={t({ en: 'Members', bn: 'সদস্য' })} testid="district-stat-members" />
            <Stat icon={Zap} value={d.stats.youth} label={t({ en: 'Youth 18-30', bn: 'তরুণ ১৮-৩০' })} testid="district-stat-youth" />
            <Stat icon={TrendingUp} value={d.stats.this_week} label={t({ en: 'Joined this week', bn: 'এই সপ্তাহে' })} testid="district-stat-week" />
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-10 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border bg-white p-6" data-testid="district-office-card">
            <h2 className="mb-4 text-lg font-bold text-gray-900">{t({ en: 'Local Office & Leaders', bn: 'স্থানীয় অফিস ও নেতৃবৃন্দ' })}</h2>
            {d.active ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <InfoRow icon={MapPin} label={t({ en: 'Office', bn: 'অফিস' })} value={c.office_address} href={c.map_url} testid="district-office" />
                <InfoRow icon={Phone} label={t({ en: 'Phone', bn: 'ফোন' })} value={c.phone} href={c.phone && `tel:${c.phone}`} testid="district-phone" />
                <InfoRow icon={Mail} label={t({ en: 'Email', bn: 'ইমেইল' })} value={c.email} href={c.email && `mailto:${c.email}`} testid="district-email" />
                <InfoRow icon={User} label={t({ en: 'President', bn: 'সভাপতি' })} value={c.president} testid="district-president" />
                <InfoRow icon={User} label={t({ en: 'General Secretary', bn: 'সাধারণ সম্পাদক' })} value={c.secretary} testid="district-secretary" />
                <InfoRow icon={Zap} label={t({ en: 'Youth Coordinator', bn: 'যুব সমন্বয়ক' })} value={c.youth_coordinator} testid="district-youth-coordinator" />
              </div>
            ) : (
              <div data-testid="district-forming-block">
                <p className="mb-4 text-gray-600">{t({ en: `The ${d.district} chapter is forming. Become a founding organiser and lead NDP in your district.`, bn: `${d.district} শাখা গঠনাধীন। প্রতিষ্ঠাতা সংগঠক হয়ে আপনার জেলায় এনডিপির নেতৃত্ব দিন।` })}</p>
                <OrganiserForm district={d.district} />
              </div>
            )}
          </div>
          <Button onClick={() => navigate(`/join?district=${encodeURIComponent(d.district)}`)} className="w-full bg-party-green py-6 text-base hover:bg-green-700" data-testid="district-join-btn">
            {t({ en: `Join NDP ${d.district}`, bn: `এনডিপি ${d.district}-এ যোগ দিন` })}
          </Button>
        </div>
        <div className="rounded-2xl border bg-white p-6" data-testid="district-top-recruiters">
          <h3 className="mb-4 flex items-center gap-2 font-bold text-gray-900"><Trophy className="h-5 w-5 text-amber-500" /> {t({ en: 'Top recruiters here', bn: 'এখানকার শীর্ষ রিক্রুটার' })}</h3>
          {d.top_recruiters.length === 0 ? <p className="text-sm text-gray-500">{t({ en: 'Be the first!', bn: 'প্রথম হন!' })}</p> : (
            <ol className="space-y-2">{d.top_recruiters.map((r, i) => <li key={i} className="flex justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm"><span>{i + 1}. {r.name}</span><b className="text-party-green">{r.count}</b></li>)}</ol>
          )}
        </div>
      </div>
    </div>
  );
}
